import React, { useRef, useState } from "react";
import { shallowEqual } from "react-redux";
import usePersonalization from "../../../../hooks/customize/usePersonalization";
import { type BuildAndBuyDispatchType, useBnbSelector } from "../../../../redux/store";
import * as Flex from "../../../../../../../common-deprecated/styles/v2/toyota/globals/Flex";
import {
    type BuildAndBuyLabelType,
    BnbLabel,
    getBnbOptionalLabel,
    useBnbOptionalLabel,
} from "../../../../utils/labels";
import * as Type from "../../../../../../../common-deprecated/styles/v2/toyota/globals/Type";
import * as Button from "../../../../../../../common-deprecated/styles/v2/toyota/components/Button";
import * as Text from "../../../../../../../common-deprecated/styles/v2/toyota/utilities/Text";
import * as Message from "../../../../../../../common-deprecated/styles/v2/toyota/components/Message";
import { IconWrapper } from "../../../../../../../common-deprecated/styles/v2/toyota/globals/IconWrapper";
import Icon, { IconCart } from "../../../../../../../common-deprecated/styles/v2/toyota/globals/Icon";
import * as TabStyles from "../../common/styles/Tabs";
import DynamicItemSlider from "../../common/DynamicItemSlider";
import * as PersonalizationStyles from "./styles/Personalisation";
import Price from "../../common/price/Price";
import CarDataConsolidator from "../../../../../../shared-logic/utils/classes/CarDataConsolidator";
import WinterWheelsPanel from "./WinterWheelsPanel";
import Popover from "../../../../../../../common-deprecated/components/popover/aem/Popover";
import { showModal } from "../../../../../../../common-deprecated/redux/actions/ModalActions";
import { isRTL } from "../../../../../../../common-deprecated/settings/utils/commonSettingUtils";
import { hideWheelPriceOnPersonalization, tyreInfoBnbLabelSelector } from "../../../../utils/typedUtils";
import { MODAL_CONTENT } from "../../../../../../../common-deprecated/utils/modalConstants";
import { trackCustomizePersonalizationItem, trackTabPersonalization } from "../../../../utils/tracking";
import type { PersonalizationType } from "../../../../../../../common-deprecated/types/CommonTypes";
import { showNextGenInteriorDisclaimer } from "../../../../utils/settings/bnbSettingUtils";
import { isOnlinePurchaseAvailable } from "../../../../../../shared-logic/utils/basketUtils";
import { useDispatch } from "../../../../../../../shared-logic/redux/store";
import { MODAL_DETAILED_TYRE_INFO } from "../../../../../../../shared-logic/utils/tyreInfoContants";

type TabsConfigType = {
    title: BuildAndBuyLabelType;
    workName: PersonalizationType;
    trackingName: "customize-colours" | "customize-wheels" | "customize-interior";
};

const Personalization = (): JSX.Element => {
    const dispatch = useDispatch<BuildAndBuyDispatchType>();
    const { onSwitch, value, options, selectItem } = usePersonalization();
    const activeTab = useBnbSelector((state) => state.utils.activePersonalization);
    const showWinterWheels = useBnbSelector(
        (state) =>
            CarDataConsolidator.getWinterWheels(state.items.winterWheels, state.basket.colour, state.basket.upholstery)
                .length > 0 && state.utils.activePersonalization === "wheel",
    );
    const showNextGenInteriorDisclaimerSetting = useBnbSelector(
        (state) =>
            showNextGenInteriorDisclaimer(state.bnbSettings, state.commonSettings, state.car.model.id) &&
            !!getBnbOptionalLabel(
                { commonSettings: state.commonSettings, bnbSettings: state.bnbSettings },
                "nextGenInteriorDisclaimer",
            ),
    );
    const tyres = useBnbSelector((state) => CarDataConsolidator.getWheelTyres(state.basket.wheel, state.items.tyres));
    const activeWheel = useBnbSelector((state) => state.basket.wheel);
    const tyreInfoLabels = useBnbSelector(tyreInfoBnbLabelSelector, shallowEqual);
    const hideWheelPrice = useBnbSelector(hideWheelPriceOnPersonalization);
    const onlinePurchaseNotPossible = useBnbSelector((state) => !isOnlinePurchaseAvailable(state.basket));

    const [
        tyreFallbackLabel,
        accessoryDisclaimer,
        genericTyreDisclaimer,
        notAvailableOnlineLabel,
        colourIncludedInPriceLabel,
        genericDisclaimerTitle,
    ] = useBnbOptionalLabel([
        "tyreInfoNoEprelFallback",
        "accessoryDisclaimer",
        "genericTyreDisclaimer",
        "notAvailableOnlineBasketWarning",
        "colourIncludedInPrice",
        "genericDisclaimerTitle",
    ]);

    const renderedDescription = `${value.description!}${accessoryDisclaimer ? `<br/>${accessoryDisclaimer}` : ""}`;

    const commonSettings = useBnbSelector((state) => state.commonSettings);

    const tabsConfig: TabsConfigType[] = [
        {
            title: "colour",
            workName: "colour",
            trackingName: "customize-colours",
        },
        {
            title: "wheels",
            workName: "wheel",
            trackingName: "customize-wheels",
        },
        {
            title: "interior",
            workName: "upholstery",
            trackingName: "customize-interior",
        },
    ];

    const tabRefs = useRef<HTMLButtonElement[]>([]);
    const defaultIndex = 0;
    const [selectedIndex, setSelectedIndex] = useState(defaultIndex);
    const handleClick = (tab: TabsConfigType, index: number): void => {
        if (activeTab !== tab.workName) {
            trackTabPersonalization(tab.trackingName);
            onSwitch(tab.workName);
        }
        setSelectedIndex(index);
    };

    const setIndex = (index: number): void => {
        const tab = tabRefs.current[index];
        if (tab) {
            // focus() will call the state setter
            // to display the associated tabpanel
            tab.focus();
        }
    };

    // Using arrow keys to navigate A11Y - arrow keys should automatically jump between tabs. wrapper should have a tabindex.
    // Which is why we work with an index for this
    const onKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>): void => {
        switch (event.key) {
            case "ArrowLeft":
                event.preventDefault();
                setIndex(selectedIndex - 1);
                break;
            case "ArrowRight":
                event.preventDefault();
                setIndex(selectedIndex + 1);
                break;
            case "Home":
                event.preventDefault();
                setIndex(0);
                break;
            case "End":
                event.preventDefault();
                setIndex(tabRefs.current.length - 1);
                break;
            default:
                break;
        }
    };

    return (
        <>
            <PersonalizationStyles.Wrapper>
                <PersonalizationStyles.Header>
                    <TabStyles.Wrapper role="tablist" aria-orientation="horizontal">
                        {tabsConfig.map((tab, index) => (
                            <TabStyles.Tab
                                key={`personalisation-tab-${tab.workName}`}
                                ref={(elem) => {
                                    tabRefs.current[index] = elem as HTMLButtonElement;
                                }}
                                px="0"
                                isActive={activeTab === tab.workName}
                                onClick={() => {
                                    handleClick(tab, index);
                                }}
                                onKeyDown={onKeyDown}
                                onFocus={() => {
                                    handleClick(tab, index);
                                }}
                                aria-controls={`personalisation-tab-${tab.workName}`}
                                aria-selected={activeTab === tab.workName}
                                id={`personalisation-tab-${tab.workName}-tab`}
                                role="tab"
                                tabIndex={selectedIndex === index ? 0 : -1}
                            >
                                <BnbLabel label={tab.title} />
                            </TabStyles.Tab>
                        ))}
                    </TabStyles.Wrapper>
                </PersonalizationStyles.Header>
                <PersonalizationStyles.Body
                    aria-labelledby={`personalisation-tab-${activeTab}-tab`}
                    id={`personalisation-tab-${activeTab}`}
                    role="tabpanel"
                    tabIndex={0}
                >
                    <Flex.Row justifyContent="flex-start" m={0}>
                        <DynamicItemSlider
                            // Key is needed to surpress the animation when switching between tabs
                            key={activeTab}
                            items={options}
                            activeItem={value}
                            activeItemType={activeTab}
                            selectItem={(item) => {
                                trackCustomizePersonalizationItem(activeTab);
                                selectItem(activeTab, item);
                            }}
                        />
                        <Flex.Col
                            width={{ xs: 1, lg: 1 / 3 }}
                            flexGrow={2}
                            pl={isRTL(commonSettings) ? "15px" : 0}
                            pr={isRTL(commonSettings) ? 0 : "15px"}
                        >
                            <PersonalizationStyles.Results>
                                <Type.SmallText>{value.name}</Type.SmallText>
                                <div>
                                    {!hideWheelPrice && <Price price={value.price} financeItemId={value.id} />}
                                    {!!genericTyreDisclaimer && activeTab === "wheel" && !activeWheel.standard && (
                                        <Button.Link
                                            type="button"
                                            hasIconRight
                                            onClick={() =>
                                                dispatch(
                                                    showModal({
                                                        type: MODAL_CONTENT,
                                                        content: genericTyreDisclaimer,
                                                    }),
                                                )
                                            }
                                        >
                                            {genericDisclaimerTitle && (
                                                <Text.Button>{genericDisclaimerTitle}</Text.Button>
                                            )}
                                            <IconWrapper>
                                                <Icon variant="info" />
                                            </IconWrapper>
                                        </Button.Link>
                                    )}
                                    {activeTab === "colour" && value.price.cash === 0 && colourIncludedInPriceLabel && (
                                        <Type.SmallHeading noUppercase>{colourIncludedInPriceLabel}</Type.SmallHeading>
                                    )}
                                </div>
                                {value.description && (
                                    <div>
                                        <BnbLabel label="moreInfo" />{" "}
                                        <Popover showOnClick content={renderedDescription} useHTML />
                                    </div>
                                )}
                                {showNextGenInteriorDisclaimerSetting && activeTab === "upholstery" && (
                                    <div>
                                        * <BnbLabel label="nextGenInteriorDisclaimer" />
                                    </div>
                                )}
                                {activeTab === "wheel" && tyreFallbackLabel && (
                                    <Button.Link
                                        color="grey7"
                                        hasIconRight
                                        onClick={(event) => {
                                            event.preventDefault();
                                            dispatch(
                                                showModal({
                                                    type: MODAL_DETAILED_TYRE_INFO,
                                                    tyres,
                                                    tyreInfoLabels,
                                                }),
                                            );
                                        }}
                                    >
                                        <Text.Button>
                                            <BnbLabel label="tyreInfo" />
                                        </Text.Button>
                                        <IconWrapper>
                                            <Icon variant="info" />
                                        </IconWrapper>
                                    </Button.Link>
                                )}
                            </PersonalizationStyles.Results>
                        </Flex.Col>
                    </Flex.Row>
                    {onlinePurchaseNotPossible && notAvailableOnlineLabel && (
                        <Message.Info>
                            <IconWrapper>
                                <IconCart />
                            </IconWrapper>
                            {notAvailableOnlineLabel}
                        </Message.Info>
                    )}
                </PersonalizationStyles.Body>
            </PersonalizationStyles.Wrapper>
            {showWinterWheels && <WinterWheelsPanel />}
        </>
    );
};

export default Personalization;
